import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import Footer from './components/Footer';
import BreakingNewsTicker from './components/BreakingNewsTicker';
import LanguageModal from './components/LanguageModal';
import { LanguageProvider } from './contexts/LanguageContext';
import { WebsiteSettingsProvider } from './contexts/WebsiteSettingsContext';

// Public pages — lazy-loaded for smaller initial bundle
const HomePage = lazy(() => import('./pages/HomePage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const DistrictPage = lazy(() => import('./pages/DistrictPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const EPaperPage = lazy(() => import('./pages/EPaperPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SocialPage = lazy(() => import('./pages/SocialPage'));
const DeveloperPage = lazy(() => import('./pages/DeveloperPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));

// Admin / panel pages — never in the public first paint
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ArticlesPage = lazy(() => import('./pages/admin/ArticlesPage'));
const CategoriesPage = lazy(() => import('./pages/admin/CategoriesPage'));
const DistrictsPage = lazy(() => import('./pages/admin/DistrictsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const MediaPage = lazy(() => import('./pages/admin/MediaPage'));
const AdvertisementsPage = lazy(() => import('./pages/admin/AdvertisementsPage'));
const EPaperAdminPage = lazy(() => import('./pages/admin/EPaperAdminPage'));
const WebsiteSettingsPage = lazy(() => import('./pages/admin/WebsiteSettingsPage'));
const WriteArticlePage = lazy(() => import('./pages/admin/WriteArticlePage'));
const ReporterPanel = lazy(() => import('./pages/ReporterPanel'));
const EditorPanel = lazy(() => import('./pages/EditorPanel'));

const PageFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center text-muted-foreground">Loading...</div>
);

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/** Shared layout — mounts once; child routes swap via Outlet (avoids refetch on nav). */
const PublicLayout = () => (
  <LanguageProvider>
    <WebsiteSettingsProvider>
      <div className="min-h-screen flex flex-col">
        <LanguageModal />
        <Header />
        <BreakingNewsTicker />
        <main className="flex-1">
          <Suspense fallback={<PageFallback />}>
            <Outlet />
          </Suspense>
        </main>
        <Footer />
      </div>
    </WebsiteSettingsProvider>
  </LanguageProvider>
);

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/article/:slug" element={<ArticlePage />} />
                <Route path="/category/:slug" element={<CategoryPage />} />
                <Route path="/district/:slug" element={<DistrictPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/epaper" element={<EPaperPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/social" element={<SocialPage />} />
                <Route path="/developer" element={<DeveloperPage />} />
              </Route>

              <Route path="/latest" element={<Navigate to="/search" replace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              <Route path="/admin" element={
                <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/articles" element={
                <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN', 'EDITOR']}>
                  <ArticlesPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/categories" element={
                <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN']}>
                  <CategoriesPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/districts" element={
                <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN']}>
                  <DistrictsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN']}>
                  <UsersPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/media" element={
                <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN']}>
                  <MediaPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/advertisements" element={
                <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN']}>
                  <AdvertisementsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/epaper" element={
                <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN']}>
                  <EPaperAdminPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/settings/website" element={
                <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN']}>
                  <WebsiteSettingsPage />
                </ProtectedRoute>
              } />

              <Route path="/reporter" element={
                <ProtectedRoute roles={['REPORTER', 'EDITOR', 'ADMIN', 'SUPER_ADMIN']}>
                  <ReporterPanel />
                </ProtectedRoute>
              } />
              <Route path="/reporter/write" element={
                <ProtectedRoute roles={['REPORTER', 'EDITOR', 'ADMIN', 'SUPER_ADMIN']}>
                  <WriteArticlePage />
                </ProtectedRoute>
              } />

              <Route path="/editor" element={
                <ProtectedRoute roles={['EDITOR', 'ADMIN', 'SUPER_ADMIN']}>
                  <EditorPanel />
                </ProtectedRoute>
              } />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
